
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import * as echarts from 'echarts';
import StackedBarChartFilterable from '@/components/reports-v3/components/filterables/StackedBarChartFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import {PermissionsGroup} from '@/store/models.def';
import { GobiColor } from '@/helpers/color';
import gdbx from '@/store/modules/gdbx';
import { DataViewFormatter } from '@/components/reports-v3/components/elements/charts/helpers/dataViewFormatter';
import { TooltipFormatter } from '@/components/reports-v3/components/elements/charts/helpers/tooltipFormatter';
import {getTableName} from '@/worker/db/sqlHelpers';

@Component({
	components: {
		StackedBarChartFilterable,
	},
})
export default class CustomerDocumentAnalysisInvoiceVsPaymentComparisonBarChart extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['customers'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = ['customers', 'agents', 'dateRange'];

	public amount: number[] = [0, 0, 0, 0, 0];
	public result: any = [];
	public xAxisData: string[] = [];
	public series: echarts.ECharts[] | any = [];

	public get dateFormatted() {
		return this.selectedDateRange;
	}

	public get currency() {
		return gdbx.currencySymbol;
	}

	public get dataViewFormatter() {
		return DataViewFormatter.barChartWithPointShare;
	}

	public get tooltipFormatter() {
		return TooltipFormatter.basicWithPointShare;
	}

	public get expensiveHook() {
		const { selectedDateRange, selectedAgents, selectedCustomers } = this;
		return JSON.stringify([
			selectedDateRange,
			selectedAgents,
			selectedCustomers,
		]);
	}

	public async expensiveCalc() {
		this.generateLoadingText(0.2, 0.3);

		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const table = getTableName('customerTrades');
		const [dateStart, dateEnd] = this.selectedDateRange;

		const allCustomerLen = (gdbx.allCustomers && gdbx.allCustomers.length) || 0;
		let customerFilter = '';
		if (this.selectedCustomers.length > 0 && this.selectedCustomers.length !== allCustomerLen) {
			const list = this.selectedCustomers.map((c) => `'${escapeStr(c)}'`).join(',');
			customerFilter = ` AND "CODE" IN (${list})`;
		}
		const allAgentLen = (gdbx.allAgents && gdbx.allAgents.length) || 0;
		let agentFilter = '';
		if (this.selectedAgents.length > 0 && this.selectedAgents.length !== allAgentLen) {
			const list = this.selectedAgents.map((a) => `'${escapeStr(a)}'`).join(',');
			agentFilter = ` AND "AGENT" IN (${list})`;
		}

		const jobId = this.currentJobId;
		const queryHead = await this._ensureTablesLoaded(['customerTrades']);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.3, 0.5);

		// Per doc type: the filtered amount (selected customers/agents) and the all-customers/agents
		// baseline (same date range, no customer/agent filter). % = filtered / baseline (contribution).
		const customerAgentCond = `1=1${customerFilter}${agentFilter}`;
		const rows = await queryHead.rawQuery(`
			SELECT "DOCTYPE" as doc_type,
				SUM(CASE WHEN ${customerAgentCond} THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as filtered_amount,
				SUM(TRY_CAST("LOCALAMOUNT" AS DOUBLE)) as total_amount
			FROM ${table}
			WHERE TRY_CAST("DOCDATE" AS BIGINT) BETWEEN ${dateStart} AND ${dateEnd}
				AND "DOCTYPE" IN ('IV','DN','PM','CN','CT')
			GROUP BY "DOCTYPE"
		`);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.9);

		// Build filtered + baseline maps from query results
		const amountMap: Record<string, number> = {};
		const totalMap: Record<string, number> = {};
		for (const row of rows) {
			amountMap[row.doc_type] = Number(row.filtered_amount) || 0;
			totalMap[row.doc_type] = Number(row.total_amount) || 0;
		}

		// Reversed order: CT, CN, PM, DN, IV
		const codeList = ['CT', 'CN', 'PM', 'DN', 'IV'];
		const nameList = ['Contra', 'Credit Note', 'Payment', 'Debit Note', 'Invoice'];

		this.amount = codeList.map((code) => amountMap[code] || 0);
		this.xAxisData = nameList;

		this.result = [];
		this.amount.forEach((item, index) => {
			const total = totalMap[codeList[index]] || 0;
			const percent = total > 0 ? (item / total) * 100 : 0;
			this.result.push({
				value: item,
				itemStyle: { color: index > 2 ? GobiColor.COLOR1 : GobiColor.COLOR3 },
				percent,
			});
		});

		this.series = [
			{
				name: 'Amount',
				data: this.result,
				type: 'bar',
				barWidth: '40%',
			},
		];

		this.generateLoadingText(1);
		this.saveHistory('series', 'xAxisData', 'result', 'amount');
	}
}
